import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { TrendingUp, Loader2, Save, Sparkles, Info, AlertCircle, ChevronDown, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, addDays, startOfDay } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOW_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = [
  { v: 1, label: 'January' }, { v: 2, label: 'February' }, { v: 3, label: 'March' },
  { v: 4, label: 'April' }, { v: 5, label: 'May' }, { v: 6, label: 'June' },
  { v: 7, label: 'July' }, { v: 8, label: 'August' }, { v: 9, label: 'September' },
  { v: 10, label: 'October' }, { v: 11, label: 'November' }, { v: 12, label: 'December' },
];

const formatMoney = (n) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 })
    .format(n || 0);

import { useAuth } from '../contexts/AuthContext';

export default function SalesForecastPage() {
  const { activeBusiness } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  // Collapsible Forecast Tables (monthly dedup + daily events) — hidden by default
  const [tablesOpen, setTablesOpen] = useState(false);
  const [tablesView, setTablesView] = useState('monthly'); // 'monthly' | 'daily'

  const [frequency, setFrequency] = useState('monthly');
  const [closeDate, setCloseDate] = useState('');
  const [dailyValues, setDailyValues] = useState(() => ({ 0: '', 1: '', 2: '', 3: '', 4: '', 5: '', 6: '' }));
  const [weeklyAmount, setWeeklyAmount] = useState('');
  const [weeklyDow, setWeeklyDow] = useState('1'); // Tue default
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [monthlyDom, setMonthlyDom] = useState('1');
  const [quarterlyAmount, setQuarterlyAmount] = useState('');
  const [quarterlyMonthInQ, setQuarterlyMonthInQ] = useState('3');
  const [quarterlyDom, setQuarterlyDom] = useState('-1');
  const [slowMonths, setSlowMonths] = useState([]);
  const [slowPct, setSlowPct] = useState('');
  const [peakMonths, setPeakMonths] = useState([]);
  const [peakPct, setPeakPct] = useState('');

  // Compute today's weekday so daily inputs start from today
  const todayDow = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1; // map JS Sun=0 → Python Sun=6
  const dailyOrder = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => (todayDow + i) % 7);
  }, [todayDow]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sales-forecast/config`);
      const c = r.data || {};
      if (c.frequency) setFrequency(c.frequency);
      if (c.close_date) setCloseDate(c.close_date);
      if (c.daily_values && Object.keys(c.daily_values).length) {
        setDailyValues({
          0: c.daily_values['0'] ?? '',
          1: c.daily_values['1'] ?? '',
          2: c.daily_values['2'] ?? '',
          3: c.daily_values['3'] ?? '',
          4: c.daily_values['4'] ?? '',
          5: c.daily_values['5'] ?? '',
          6: c.daily_values['6'] ?? '',
        });
      }
      if (c.weekly_amount != null) setWeeklyAmount(String(c.weekly_amount));
      if (c.weekly_recognition_dow != null) setWeeklyDow(String(c.weekly_recognition_dow));
      if (c.monthly_amount != null) setMonthlyAmount(String(c.monthly_amount));
      if (c.monthly_reporting_dom != null) setMonthlyDom(String(c.monthly_reporting_dom));
      if (c.quarterly_amount != null) setQuarterlyAmount(String(c.quarterly_amount));
      if (c.quarterly_month_in_quarter != null) setQuarterlyMonthInQ(String(c.quarterly_month_in_quarter));
      if (c.quarterly_reporting_dom != null) setQuarterlyDom(String(c.quarterly_reporting_dom));
      if (Array.isArray(c.slow_months)) setSlowMonths(c.slow_months);
      if (c.slow_pct != null) setSlowPct(String(c.slow_pct));
      if (Array.isArray(c.peak_months)) setPeakMonths(c.peak_months);
      if (c.peak_pct != null) setPeakPct(String(c.peak_pct));
    } catch (e) {
      // ignore - first-load fresh
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadConfig();
      // Auto-load the preview so users see their numbers without having to
      // re-save every time they navigate away and back to this page.
      await loadPreview();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMonth = (which, m) => {
    if (which === 'slow') {
      setSlowMonths((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);
    } else {
      setPeakMonths((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);
    }
  };

  const buildPayload = () => {
    const payload = {
      frequency,
      close_date: closeDate || null,
      slow_months: slowMonths,
      slow_pct: parseFloat(slowPct) || 0,
      peak_months: peakMonths,
      peak_pct: parseFloat(peakPct) || 0,
    };
    if (frequency === 'daily') {
      const dv = {};
      Object.entries(dailyValues).forEach(([k, v]) => {
        const num = parseFloat(v);
        if (!Number.isNaN(num)) dv[k] = num;
      });
      payload.daily_values = dv;
    } else if (frequency === 'weekly') {
      payload.weekly_amount = parseFloat(weeklyAmount) || 0;
      payload.weekly_recognition_dow = parseInt(weeklyDow, 10);
    } else if (frequency === 'monthly') {
      payload.monthly_amount = parseFloat(monthlyAmount) || 0;
      payload.monthly_reporting_dom = parseInt(monthlyDom, 10);
    } else if (frequency === 'quarterly') {
      payload.quarterly_amount = parseFloat(quarterlyAmount) || 0;
      payload.quarterly_month_in_quarter = parseInt(quarterlyMonthInQ, 10);
      payload.quarterly_reporting_dom = parseInt(quarterlyDom, 10);
    }
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sales-forecast/config`, buildPayload());
      toast.success('Forecast saved');
      await loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/sales-forecast/preview`);
      setPreview(r.data);
    } catch (e) {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderMonthChips = (which, selected) => (
    <div className="flex flex-wrap gap-1.5">
      {MONTHS.map((m) => {
        const active = selected.includes(m.v);
        return (
          <button
            key={m.v}
            type="button"
            onClick={() => toggleMonth(which, m.v)}
            data-testid={`sf-${which}-month-${m.v}`}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
              active
                ? which === 'slow'
                  ? 'bg-red-50 border-red-300 text-red-700'
                  : 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            {m.label.slice(0, 3)}
          </button>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (activeBusiness?.is_consolidated) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="max-w-md text-center space-y-3" data-testid="consolidated-blocked-sales-forecast">
            <TrendingUp className="w-10 h-10 text-slate-300 mx-auto" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Sales Forecast is per-business
            </h2>
            <p className="text-sm text-slate-500">
              Switch to a specific business from the sidebar to configure or view a Sales Forecast.
              Consolidated view aggregates existing forecasts into the main dashboard.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <TrendingUp className="w-6 h-6 text-primary" />
              Sales Forecast
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Model future sales alongside your Xero invoices. Cash receipts and COGS payments use your dashboard's payment-cycle averages.
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="sf-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save &amp; Preview
          </Button>
        </div>

        {/* Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Forecast Configuration</CardTitle>
              <CardDescription>Pick a frequency. Fields below adapt to it.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Frequency + close date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Forecast frequency</Label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger data-testid="sf-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Forecast close date <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input
                    type="date"
                    value={closeDate}
                    onChange={(e) => setCloseDate(e.target.value)}
                    data-testid="sf-close-date"
                  />
                  <p className="text-[10px] text-slate-400">Defaults to the end of your 13-month forecast window.</p>
                </div>
              </div>

              {/* Daily */}
              {frequency === 'daily' && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Expected sales per weekday (starting today)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {dailyOrder.map((dow) => (
                      <div key={dow} className="space-y-1">
                        <Label className="text-[11px] text-slate-500">{DOW_LONG[dow]}</Label>
                        <Input
                          type="number"
                          step="any"
                          placeholder="$0"
                          value={dailyValues[dow]}
                          onChange={(e) => setDailyValues((p) => ({ ...p, [dow]: e.target.value }))}
                          data-testid={`sf-daily-${dow}`}
                          className="h-9 font-mono"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Weekly */}
              {frequency === 'weekly' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                  <div className="space-y-1.5">
                    <Label>Expected weekly sales</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="$0"
                      value={weeklyAmount}
                      onChange={(e) => setWeeklyAmount(e.target.value)}
                      data-testid="sf-weekly-amount"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Day of week to recognise</Label>
                    <Select value={weeklyDow} onValueChange={setWeeklyDow}>
                      <SelectTrigger data-testid="sf-weekly-dow"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOW_LONG.map((d, i) => (
                          <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Monthly */}
              {frequency === 'monthly' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                  <div className="space-y-1.5">
                    <Label>Expected monthly sales</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="$0"
                      value={monthlyAmount}
                      onChange={(e) => setMonthlyAmount(e.target.value)}
                      data-testid="sf-monthly-amount"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Reporting date each month</Label>
                    <Select value={monthlyDom} onValueChange={setMonthlyDom}>
                      <SelectTrigger data-testid="sf-monthly-dom"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</SelectItem>
                        ))}
                        <SelectItem value="-1">Last day of month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Quarterly */}
              {frequency === 'quarterly' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
                  <div className="space-y-1.5">
                    <Label>Expected quarterly sales</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="$0"
                      value={quarterlyAmount}
                      onChange={(e) => setQuarterlyAmount(e.target.value)}
                      data-testid="sf-quarterly-amount"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Month in each quarter</Label>
                    <Select value={quarterlyMonthInQ} onValueChange={setQuarterlyMonthInQ}>
                      <SelectTrigger data-testid="sf-quarterly-miq"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1st month</SelectItem>
                        <SelectItem value="2">2nd month</SelectItem>
                        <SelectItem value="3">3rd (last) month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Day of that month</Label>
                    <Select value={quarterlyDom} onValueChange={setQuarterlyDom}>
                      <SelectTrigger data-testid="sf-quarterly-dom"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</SelectItem>
                        ))}
                        <SelectItem value="-1">Last day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Seasonal */}
              <div className="pt-3 border-t border-slate-100 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Slower months</Label>
                  {renderMonthChips('slow', slowMonths)}
                  <div className="flex items-center gap-2 mt-2">
                    <Label className="text-xs text-slate-500">% lower than normal</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="0"
                      value={slowPct}
                      onChange={(e) => setSlowPct(e.target.value)}
                      className="h-8 w-24 font-mono"
                      data-testid="sf-slow-pct"
                    />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Peak months</Label>
                  {renderMonthChips('peak', peakMonths)}
                  <div className="flex items-center gap-2 mt-2">
                    <Label className="text-xs text-slate-500">% higher than normal</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="0"
                      value={peakPct}
                      onChange={(e) => setPeakPct(e.target.value)}
                      className="h-8 w-24 font-mono"
                      data-testid="sf-peak-pct"
                    />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right-rail summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Cash-flow Settings
              </CardTitle>
              <CardDescription>Sourced from your dashboard's Payment Cycle &amp; Margin card.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {preview?.settings ? (
                <>
                  <Row label="Avg days to receive" value={`${preview.settings.dso_days ?? 0} days`} />
                  <Row label="Avg days to pay" value={`${preview.settings.dpo_days ?? 0} days`} />
                  <Row label="Gross margin" value={preview.settings.gross_margin_percent != null ? `${preview.settings.gross_margin_percent}%` : '—'} />
                  <Row label="COGS ratio" value={`${preview.settings.cogs_ratio_percent}%`} />
                  <Row label="Window" value={`${preview.settings.forecast_start} → ${preview.settings.forecast_end}`} small />
                </>
              ) : (
                <p className="text-xs text-slate-500 flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Save the forecast to see how these numbers feed into the cash-flow dates.
                </p>
              )}
              {preview?.settings && (preview.settings.dso_days === 0 || preview.settings.gross_margin_percent == null) && (
                <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-xs flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Some payment-cycle metrics aren't set. Run a Xero sync or set them manually on the dashboard for accurate cash dates.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview totals */}
        {preview && (
          <Card data-testid="sf-preview-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Forecast Preview</CardTitle>
                <CardDescription>
                  {preview.events?.length || 0} events over your forecast window. Phase 2 will feed these into the dashboard cash-flow chart automatically.
                </CardDescription>
              </div>
              {previewLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total forecast sales" value={formatMoney(preview.totals?.sales)} accent="emerald" />
                <Stat label="Total estimated COGS" value={formatMoney(preview.totals?.cogs)} accent="red" />
                <Stat label="Net margin" value={formatMoney(preview.totals?.net_margin)} accent="blue" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Forecast Tables — collapsed by default. Show on demand via dropdown */}
        {preview && (
          <Card data-testid="sf-tables-card">
            <CardHeader className="cursor-pointer select-none" onClick={() => setTablesOpen(o => !o)}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    Forecast Tables
                  </CardTitle>
                  <CardDescription>
                    {tablesOpen
                      ? 'Switch between monthly and daily forecast below'
                      : 'Net-of-Xero deduplication & daily events — click to expand'}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={(e) => { e.stopPropagation(); setTablesOpen(o => !o); }}
                  data-testid="sf-toggle-tables-btn"
                >
                  {tablesOpen ? 'Hide' : 'Show'}
                  <ChevronDown className={`w-4 h-4 transition-transform ${tablesOpen ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </CardHeader>

            {tablesOpen && (
              <CardContent className="space-y-4">
                {/* View selector */}
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">View</Label>
                  <Select value={tablesView} onValueChange={setTablesView}>
                    <SelectTrigger className="w-[260px] h-9" data-testid="sf-tables-view-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly" data-testid="sf-tables-view-monthly">Monthly (Net-of-Xero deduplication)</SelectItem>
                      <SelectItem value="daily" data-testid="sf-tables-view-daily">Daily events</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Monthly view */}
                {tablesView === 'monthly' && (
                  <div data-testid="sf-tables-view-monthly-panel">
                    {preview.dedup_by_month && Object.keys(preview.dedup_by_month).length > 0 ? (
                      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                          <Info className="w-3.5 h-3.5 text-slate-500" />
                          <p className="text-xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Net-of-Xero Deduplication — per month</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs" data-testid="sf-dedup-table">
                            <thead>
                              <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase tracking-wider">Month</th>
                                <th className="text-right px-3 py-2 font-medium text-slate-500 uppercase tracking-wider">Forecast Sales</th>
                                <th className="text-right px-3 py-2 font-medium text-slate-500 uppercase tracking-wider" title="Sum of Xero invoice amounts raised in this month">Xero Inv. Raised</th>
                                <th className="text-right px-3 py-2 font-medium text-slate-500 uppercase tracking-wider" title="Forecasted cash inflow (sales) flowing into the dashboard">Fc. In (sales)</th>
                                <th className="text-right px-3 py-2 font-medium text-slate-500 uppercase tracking-wider">Forecast COGS</th>
                                <th className="text-right px-3 py-2 font-medium text-slate-500 uppercase tracking-wider" title="Sum of Xero bill amounts raised in this month (AP)">Xero Bills Raised</th>
                                <th className="text-right px-3 py-2 font-medium text-slate-500 uppercase tracking-wider" title="Forecasted cash outflow (COGS) flowing into the dashboard">Fc. Out (COGS)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(preview.dedup_by_month).sort().map(([m, v]) => {
                                const cogsGross = v.forecast_cogs_gross ?? (v.forecast_gross * ((preview.settings?.cogs_ratio_percent || 0) / 100));
                                const cogsApplied = v.forecast_out_applied ?? cogsGross;
                                const billsRaised = v.xero_bills_raised || 0;
                                return (
                                  <tr key={m} className="border-t border-slate-100 dark:border-slate-700" data-testid={`sf-dedup-row-${m}`}>
                                    <td className="px-3 py-1.5 font-mono">{m}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{formatMoney(v.forecast_gross)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono text-red-600">−{formatMoney(v.xero_raised)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono font-semibold text-emerald-700">{formatMoney(v.forecast_applied)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{formatMoney(cogsGross)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono text-red-600">−{formatMoney(billsRaised)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono font-semibold text-red-700">{formatMoney(cogsApplied)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <p className="px-3 py-2 text-[10px] text-slate-400 bg-slate-50/30 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
                          Forecast inflow is reduced (never below zero) by Xero <strong>invoices</strong> raised that month. Forecast outflow is reduced by Xero <strong>bills</strong> raised that month. This prevents double-counting on both sides.
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 py-6 text-center">No monthly deduplication data yet.</p>
                    )}
                  </div>
                )}

                {/* Daily events view */}
                {tablesView === 'daily' && (
                  <div data-testid="sf-tables-view-daily-panel">
                    {preview.events && preview.events.length > 0 ? (
                      <div className="overflow-x-auto rounded-md border border-slate-200">
                        <Table data-testid="sf-events-table">
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500">Sale Date</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Sales</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cash Received</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">COGS</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-slate-500">COGS Paid</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {preview.events.map((ev, i) => (
                              <TableRow key={i} data-testid={`sf-event-row-${i}`}>
                                <TableCell className="font-mono text-xs whitespace-nowrap">
                                  {format(parseISO(ev.sale_date), 'EEE dd MMM yyyy')}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-emerald-700">
                                  {formatMoney(ev.sales_amount)}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-slate-600">
                                  {format(parseISO(ev.cash_received_date), 'dd MMM yyyy')}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm text-red-700">
                                  {formatMoney(ev.cogs_amount)}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-slate-600">
                                  {format(parseISO(ev.cogs_payment_date), 'dd MMM yyyy')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 py-6 text-center">No events yet. Fill in the form above and Save.</p>
                    )}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}

function Row({ label, value, small = false }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`font-mono font-semibold ${small ? 'text-[11px] text-slate-500' : 'text-sm text-slate-800 dark:text-slate-100'}`}>
        {value}
      </span>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const colors = {
    emerald: 'border-emerald-200/60 text-emerald-700',
    red: 'border-red-200/60 text-red-700',
    blue: 'border-blue-200/60 text-blue-700',
  }[accent] || '';
  return (
    <div className={`rounded-lg border ${colors} bg-white dark:bg-slate-900 p-3`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="text-xl font-bold font-mono mt-0.5">{value}</p>
    </div>
  );
}
